/**
 * @voltxampere/components — Digital logic gate (Sprint 41)
 *
 * Sprint 41 introduces the `digital-gate` canonical engine type to
 * land the Sprint 40 carry-over of 12 logic IC variants without
 * decomposing each gate into op-amp + comparator subcircuits. One
 * stamp covers all combinational gates (`gateFunction` parameter
 * dispatches to a truth-table registry). The 7474 D-flop variant
 * ships with `gateFunction: "D-FLOP"` but the DC stamp returns the
 * `vLow` rail because sequential clock-edge simulation needs
 * transient state tracking — that lands in Sprint 42+.
 *
 * Design parameters (all engine-visible):
 *
 *   - `gateFunction`: enum over the canonical six combinational
 *     functions plus the D-FLOP placeholder. Adding a new gate is
 *     a one-line registry update + this enum extension.
 *   - `vHigh` / `vLow`: output rail voltages. TTL parts default to
 *     2.4 V / 0.4 V; CMOS parts default to ≈ Vcc / 0 V depending on
 *     supply.
 *   - `threshold`: input voltage above which a pin reads HIGH. TTL
 *     mid-point ≈ 1.4 V, CMOS at Vcc=5 V ≈ 2.5 V.
 *   - `propagationDelay`: typed for forward-compat. The DC stamp
 *     ignores it; Sprint 42+ transient stamp will respect it.
 *
 * Pin layout: two inputs + one output + a Vcc/GND placeholder pair
 * the canvas can route from later. The DC stamp itself only uses
 * `in1`, `in2` (2-input), and `out`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const DIGITAL_GATE_FUNCTIONS = ["NAND", "NOR", "AND", "OR", "XOR", "NOT", "D-FLOP"];
/**
 * Sprint 43 (ADR-0064) — Output rail behaviour selector.
 *
 * - `"standard"` (default) preserves Sprint 41 combinational behaviour
 *   bit-identical: input `vIn >= threshold` → boolean → truth table.
 * - `"schmitt"` activates the inverting Schmitt-trigger branch in the
 *   engine stamp. Two thresholds (`vThresholdPlus` / `vThresholdMinus`)
 *   define the hysteresis window; the stamp reads `xPrev[output]` to
 *   decide which threshold applies, producing the two-fixed-point
 *   behaviour real Schmitt parts exhibit. See ADR-0064 for the
 *   self-consistent fixed-point algorithm.
 */
export const DIGITAL_GATE_OUTPUT_TYPES = ["standard", "schmitt"];
export const DigitalGateParamsSchema = z
    .object({
    gateFunction: z.enum(DIGITAL_GATE_FUNCTIONS).default("NAND"),
    vHigh: z.number().finite().default(5),
    vLow: z.number().finite().default(0),
    threshold: z.number().finite().default(2.5),
    propagationDelay: z.number().nonnegative().finite().default(10e-9),
    /**
     * Sprint 43 (ADR-0064) — Output behaviour selector. Default
     * `"standard"` preserves the Sprint 41 combinational path verbatim
     * so existing fixtures and the 16 frozen sentinels stay
     * bit-identical.
     */
    outputType: z.enum(DIGITAL_GATE_OUTPUT_TYPES).default("standard"),
    /**
     * Sprint 43 (ADR-0064) — Upper threshold V_T+. When the gate is
     * currently outputting LOW the input must rise above V_T+ to flip
     * HIGH. TI SN74HC14 datasheet typical at Vcc=5 V is 1.7 V; the
     * default matches.
     */
    vThresholdPlus: z.number().finite().default(1.7),
    /**
     * Sprint 43 (ADR-0064) — Lower threshold V_T-. When the gate is
     * currently outputting HIGH the input must fall below V_T- to flip
     * LOW. TI SN74HC14 datasheet typical at Vcc=5 V is 0.9 V.
     */
    vThresholdMinus: z.number().finite().default(0.9),
})
    .strict();
export const digitalGateDefinition = {
    type: "digital-gate",
    category: "active",
    displayName: { tr: "Lojik Kapı", en: "Logic Gate" },
    symbol: "U",
    pins: [
        { id: "in1", side: "left", offset: 0, label: "A" },
        { id: "in2", side: "left", offset: 1, label: "B" },
        { id: "out", side: "right", offset: 0, label: "Y" },
    ],
    parameterSchema: DigitalGateParamsSchema,
    // Threshold-driven evaluation is non-smooth, so flag as nonlinear
    // even though the stamp itself is a value-driven voltage source —
    // Newton-Raphson must iterate to settle when the boolean output
    // depends on the previous iterate's input voltage.
    nonlinear: true,
};
//# sourceMappingURL=digital-gate.js.map